/**
 * US phone formatting + E.164 normalisation, shared by both SPAs.
 *
 * Phone numbers were typed into plain text boxes with no shape at all: a client
 * could enter seven digits, fifteen digits, dots, or a leading 001, and whatever
 * they typed is what got stored. The columns are named `phone_e164`, so anything
 * that is not E.164 in them is simply wrong — and the same number then displayed
 * differently on every screen depending on how it was keyed.
 *
 * The platform is US-only today (Texas brokerages, Twilio US DIDs), so the
 * default is +1 and the display format is the US national one. A number typed
 * with an explicit non-US country code is passed through rather than mangled:
 * silently truncating a foreign number to ten digits would lock a real person
 * out of their own profile, which is worse than an unformatted string.
 *
 * ⚠️ `toE164US` deliberately mirrors `re_platform_core.phone.normalize_e164`
 * rule for rule (already-`+8..15` kept, 10 digits → +1, 11 leading 1 → +, else
 * reject). The phone IS the cross-tenant identity key for a dual-licensed agent
 * — dual detection and DID lookup both match on `users.phone_e164` — so a
 * client that normalised differently from the server would produce two
 * "different" numbers for one person and quietly break that match. Change the
 * two together.
 */

/** Digits only, e.g. "(512) 555-0142" → "5125550142". */
function digitsOf(input: string): string {
  return input.replace(/\D/g, "");
}

/** True when the user is explicitly entering a non-US country code. */
function isForeign(input: string): boolean {
  const trimmed = input.trim();
  if (!trimmed.startsWith("+")) return false;
  return !trimmed.startsWith("+1");
}

/**
 * E.164 for storage: "(512) 555-0142" → "+15125550142".
 *
 * Returns null when the input cannot be a US number yet — an incomplete entry
 * is not an error to shout about, it is just not ready to save.
 */
export function toE164US(input: string): string | null {
  if (!input.trim()) return null;
  if (isForeign(input)) {
    const foreign = digitsOf(input);
    return foreign.length >= 8 ? `+${foreign}` : null;
  }
  let digits = digitsOf(input);
  // A US number keyed with its country code, or with the old long-distance 1.
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.length === 10 ? `+1${digits}` : null;
}

/**
 * Display: "+15125550142" → "(512) 555-0142".
 *
 * Anything that is not a recognisable US number is returned verbatim — a stored
 * value we cannot parse must still be readable on screen, because hiding it is
 * how a bad row goes unnoticed.
 */
export function formatPhoneUS(phone: string | null | undefined): string {
  if (!phone) return "";
  if (isForeign(phone)) return phone;
  const digits = digitsOf(phone);
  const ten =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length !== 10) return phone;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * What to show in the input WHILE typing — formats progressively so the shape
 * appears as the digits land, rather than snapping into place at the tenth.
 */
export function formatPhoneInput(input: string): string {
  if (isForeign(input)) return input.trim();
  let digits = digitsOf(input);
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10); // the cap that stops "any number of digits"
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Complete enough to store. Empty counts as valid — most phone fields are optional. */
export function isValidPhone(input: string): boolean {
  return !input.trim() || toE164US(input) !== null;
}
